<template>
  <div class="address-contacts">
    <transition name="view" mode="out-in">
      <c-loader v-if="loading" :size="isMobile ? 79 : 99" />

      <form-group
        v-else
        ref="formGroup"
        :label-left="!isMobile"
        :schemas="schemas"
        :data="data"
        :options="options"
        :disabled="submitting"
        :required-fields="requiredFields"
        @add:emergency="onAddEmergency"
        @remove:emergency="onRemoveEmergency"
        @file:add="onFileModalOpen"
        @file:remove="onRemoveFile"
      />
    </transition>

    <dialog-modal
      v-if="removing"
      theme="error"
      title="Deseja excluir este contato de emergência?"
      message="As informações inseridas serão excluídas do formulário.
      Tem certeza que deseja excluir este registro?"
      :loading="submitting"
      @cancel="onRemoveEmergencyCancel"
      @confirm="onRemoveEmergencySubmit"
    />

    <c-form-builder-modal
      v-if="showFileModal"
      label-left
      submit-button-text="Salvar"
      name="address"
      title="Anexar Arquivos"
      v-model="filesFormData"
      :required-fields="requiredFields.address"
      :is-loading="submitting"
      :is-disabled="submitting"
      :schema="filesSchema"
      @close="onFileModalClose"
      @submit="onFileModalSubmit"
    />

    <dialog-modal
      v-if="showRemoveFileModal"
      theme="error"
      title="Deseja prosseguir?"
      message="Ao prosseguir os arquivos apagados serão permanentemente excluídos,
        esta ação não pode ser desfeita."
      confirm="Excluir"
      cancel="Não, mudei de ideia"
      @cancel="showRemoveFileModal = false"
      @confirm="onFileModalSubmit(filesFormData)"
    />

    <dialog-modal
      v-if="isRemovingFile"
      theme="error"
      title="Deseja excluir o arquivo selecionado?"
      message="O arquivo será permanentemente excluído, esta ação não pode ser desfeita."
      confirm="Excluir"
      cancel="Não, mudei de ideia"
      :loading="submitting"
      @cancel="isRemovingFile = false"
      @confirm="onRemoveFileSubmit"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { MediaQuery } from '@convenia/mixins'
import * as types from '@types'

import FormGroup from '@components/FormGroup'
import DialogModal from '@components/DialogModal'
import contactSchemas from '@forms/contact'

export default {
  name: 'AddressContacts',

  mixins: [ MediaQuery ],

  components: {
    FormGroup,
    DialogModal
  },

  data: () => ({
    loading: true,
    removing: false,
    activeEmergency: null,
    cities: [],
    showFileModal: false,
    showRemoveFileModal: false,
    isRemovingFile: false,
    removingFile: null,
    filesFormData: {},
  }),

  computed: {
    ...mapState({
      address: state => state.contact.address,
      contact: state => state.contact.contact,
      emergency: state => state.contact.emergency,
      states: state => state.location.states,
      customFields: state => state.contact.customFields,
      employeeId: state => state.admission.employeeUuid,
    }),

    ...mapGetters({
      getRequiredFields: types.EMPLOYEE_GET_REQUIRED_SYSTEM_FIELDS
    }),

    options () {
      const { options } = this.$store.state.contact || {}

      return {
        ...options,
        address: {
          ...(options.address || {}),
          state: this.states,
          city: this.cities
        }
      }
    },

    data () {
      return {
        address: this.address,
        contact: this.contact,
        emergency: this.emergency,
      }
    },

    schemas () {
      const { contact, address, emergency } = contactSchemas(this)

      return {
        address: {
          ...address,
          fields: {
            ...address.fields,
            ...this.customFields.address.schema,
            files: {
              ...address.fields.files,
              hide: true
            }
          }
        },
        contact: {
          ...contact,
          fields: {
            ...contact.fields,
            ...this.customFields?.contact?.schema || {}
          }
        },
        emergency: {
          ...emergency,
          fields: {
            ...emergency.fields,
            ...this.customFields?.emergencyContact?.schema || {}
          }
        }
      }
    },

    filesSchema () {
      const { address } = contactSchemas(this)
      const { files } = address.fields

      return { files }
    },

    selectedEmergency () {
      return this.emergency.find(({ id }) => id === this.activeEmergency)
    },

    submitting: {
      get () { return this.$store.state.admission.submitting },
      set (value) { this.$store.commit(types.ADMISSION_SUBMITTING, value) }
    },

    requiredFields () {
      return {
        address: this.getRequiredFields('Address'),
        contact: this.getRequiredFields('ContactInformation'),
        emergency: this.getRequiredFields('EmergencyContact'),
      }
    },
  },

  mounted () {
    this.$root.$on('next', this.onSubmit)
    this.$root.$on('previous', this.onSave)
    this.init()
  },

  beforeDestroy () {
    this.$root.$off('next', this.onSubmit)
    this.$root.$off('previous', this.onSave)
  },

  methods: {
    ...mapMutations({
      saveAddress: types.EMPLOYEE_ADDRESS_SET,
      saveContact: types.EMPLOYEE_CONTACT_SET,
      saveEmergency: types.EMPLOYEE_EMERGENCY_CONTACT_SET,
      addEmergency: types.EMPLOYEE_EMERGENCY_CONTACT_ADD
    }),

    ...mapActions({
      getCustomFields: types.EMPLOYEE_ADDRESS_CUSTOM_FIELDS,
      getAddress: types.EMPLOYEE_ADDRESS,
      getContacts: types.EMPLOYEE_CONTACTS,
      getEmergencyContacts: types.EMPLOYEE_EMERGENCY_CONTACTS,
      getOptions: types.EMPLOYEE_ADDRESS_CONTACTS_OPTIONS,
      getStates: types.LOCATION_STATES,
      getCities: types.LOCATION_CITIES,
      setAddress: types.EMPLOYEE_ADDRESS_SET,
      setContact: types.EMPLOYEE_CONTACT_SET,
      setEmergency: types.EMPLOYEE_EMERGENCY_CONTACT_SET,
      deleteEmergencyContact: types.EMPLOYEE_EMERGENCY_CONTACT_DELETE,
      deleteFile: types.ADMISSION_DELETE_FILE,
      uploadFiles: types.EMPLOYEE_ADDRESS_UPLOAD_FILES,
      getRequiredSystemFields: types.EMPLOYEE_GET_REQUIRED_SYSTEM_FIELDS,
    }),

    async init () {
      await Promise.allSettled([
        this.getAddress(),
        this.getContacts(),
        this.getEmergencyContacts(),
        this.getOptions(),
        this.getStates(),
        this.getRequiredSystemFields(),
      ])

      await this.getCustomFields()

      if (this.data.address.stateId)
        this.cities = await this.getCities(this.data.address.stateId)

      this.loading = false
    },

    onAddEmergency () {
      this.addEmergency()
      this.$refs.formGroup.formsData.emergency.push(
        this.data.emergency[this.data.emergency.length - 1]
      )
    },

    onRemoveEmergency (id) {
      this.activeEmergency = id

      const data = this.$refs.formGroup.formsData
      const emergency = data.emergency.find(
        (emergency) => emergency.id === this.activeEmergency
      ) || {}
      const hasContent = Object
        .entries(emergency)
        .filter(([ key ]) => key !== 'id')
        .some(([ , value ]) => !!value)

      if (!hasContent)
        return this.onRemoveEmergencySubmit()

      this.removing = true
    },

    onRemoveEmergencyCancel () {
      this.activeEmergency = null
      this.removing = false
    },

    async onRemoveEmergencySubmit () {
      this.submitting = true
      await this.deleteEmergencyContact(this.selectedEmergency)
      this.submitting = false

      this.$refs.formGroup.removeFormDataItem('emergency', this.activeEmergency)

      this.activeEmergency = null
      this.removing = false
    },

    async validate () {
      await this.getRequiredSystemFields()

      if (!this.$refs.formGroup)
        return false

      if (!await this.$refs.formGroup.validate())
        return false

      return true
    },

    async onSubmit () {
      if (!await this.validate()) return

      const data = this.$refs.formGroup.formsData

      const isNotEmpty = data => Object.values(data)
        .some(value => Array.isArray(value) ? value.length : !!value)

      const emptyEmergencies = (data.emergency || [])
        .filter(({ id, files, documentFiles, ...fields }) => typeof id === 'string' && !isNotEmpty(fields))

      const emergencyContacts = (data.emergency || [])
        .filter(({ id, files, documentFiles, ...fields }) => isNotEmpty(fields))

      const hasInvalidEmergency = emergencyContacts.some(({ id, name, ...rest }) => {
        const hasOtherFieldFilled = Object.values(rest).some(value => !!value)
        return hasOtherFieldFilled && !name?.trim()
      })

      if (hasInvalidEmergency)
        return this.$feedback.add({
          type: 'error',
          message: 'Contatos de Emergência.',
          highlighted: 'Obrigatório preencher o campo nome.'
        })

      this.submitting = true

      Promise.allSettled(emptyEmergencies.map(this.deleteEmergencyContact))

      const results = await Promise.allSettled([
        this.setAddress(data.address),
        this.setContact(data.contact),
        this.setEmergency(emergencyContacts),
      ])

      if (!this.emergency?.length) {
        const emptyEmergency = { id: Date.now() }

        this.saveEmergency([ emptyEmergency ])
        this.$refs.formGroup.formsData.emergency = [ emptyEmergency ]
      }

      const hasErrors = results.some(({ value: [ err ] = [] } = {}) =>
        Array.isArray(err) ? err.length : err
      )

      this.submitting = false

      if (hasErrors)
        return this.$feedback.add({
          type: 'error',
          message: 'Erro',
          highlighted: 'ao salvar Endereços e Contatos. Tente novamente.'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Endereços e contatos',
        highlighted: 'salvos com sucesso.'
      })

      this.$emit('next')
    },

    onSave () {
      if (!this.$refs.formGroup) return
      const data = this.$refs.formGroup.formsData

      this.saveAddress(data.address)
      this.saveContact(data.contact)
      this.saveEmergency(data.emergency || [])

      this.$emit('previous')
    },

    async deleteFiles (files) {
      const { employeeId } = this

      await Promise.all(files.map(
        file => this.deleteFile({ file, employeeId }))
      )
    },

    onFileModalOpen (index) {
      const data = this.$refs.formGroup?.formsData.address

      if (!data) return

      this.active = index

      this.filesFormData.files = [ ...data.files || [] ]
      this.showFileModal = true
    },

    onFileModalClose () {
      this.filesFormData = {}
      this.showFileModal = false
    },

    onRemoveFile (file) {
      this.isRemovingFile = true
      this.removingFile = file
    },

    async onRemoveFileSubmit () {
      const { employeeId } = this

      this.submitting = true

      const [ err ] = await this.deleteFile({
        employeeId,
        file: this.removingFile
      })

      this.submitting = false

      if (err)
        return this.$feedback.add({
          type: 'error',
          message: 'Arquivo',
          highlighted: 'não foi excluído, tente novamente'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Arquivo',
        highlighted: 'excluído com sucesso'
      })

      const data = this.$refs.formGroup?.formsData.address || {}
      const fileIndex = data.files?.findIndex(file => file.id === this.removingFile.id)

      if (fileIndex >= 0)
        data.files.splice(fileIndex, 1)

      this.isRemovingFile = false
    },

    async onFileModalSubmit ({ files }) {
      const data = this.$refs.formGroup?.formsData.address || {}
      const { files: initialFiles = [] } = data

      const addedFiles = files
        .filter(file => !file.done)
        .filter(file => !initialFiles.includes(file))

      const removedFiles = initialFiles
        .filter(file => !files.includes(file))

      if (removedFiles.length > 0 && !this.showRemoveFileModal) {
        this.filesFormData = { files }
        this.showRemoveFileModal = true

        return
      }

      this.showRemoveFileModal = false
      this.submitting = true

      const [ err, address ] = await this.setAddress({
        ...this.address,
        ...data,
      })

      if (err) {
        this.submitting = false
        return
      }

      await this.deleteFiles(removedFiles)

      const [ uploadErr ] = await this.uploadFiles({
        id: address?.id,
        files: addedFiles,
      })

      if (uploadErr) {
        this.submitting = false
        return
      }

      const updatedFiles = [ ...initialFiles, ...addedFiles ]
        .filter(file => !removedFiles.includes(file))
        .map(file => ({ ...file, progress: 0 }))

      data.files = updatedFiles

      this.filesFormData = {}
      this.showFileModal = false
      this.submitting = false
    },
  }
}
</script>

<style lang="scss">
.address-contacts {
  position: relative;

  @include responsive (tablet, desktop) {
    margin-top: 10px;
    background: white;
    border-radius: 5px;
    box-shadow: 2px 8px 16px 0px rgba(0, 0, 0, 0.06);
  }
}
</style>
